import { Component } from 'react'
import { Box, Button, Input, KeyValueRow, Modal, Spacer, Switch, ModalTitle } from 'stylewhere/components'
import { T, __ } from 'stylewhere/i18n'
import { AppStore, Router } from 'stylewhere/shared'
import { sleep, enabledUtilities, getLanguages, showToastError } from 'stylewhere/utils'
import bcrypt from 'bcryptjs'

// admin mode password
const HARDCODED_PASS = '$2b$10$FF3IltRF7u0D0Su8C218g.s1kBKgLtxylkVLHRYyfS/277M5AWdFi'

interface PasswordValidate {
  valid: boolean
  message: string
}

interface Props {
  onClose: () => void
  visible: boolean
}

interface State {
  admin: boolean
  endpoint: string
}

export class SettingsModal extends Component<Props, State> {
  languages = getLanguages()
  state: State = { admin: false, endpoint: '' }

  componentDidMount() {
    this._init()
  }

  _init = async () => {
    const endpointUrl = await AppStore.getEndpoint()
    const adminMode = await AppStore.getAdminMode()
    this.setState({ endpoint: endpointUrl, admin: adminMode })
  }

  handleChange = (item: any) => {
    AppStore.changeLanguage(item.value)
    sleep(100).then(() => Router.refresh())
  }

  validateFields = async (pwd: string) => {
    const result: PasswordValidate = {
      valid: false,
      message: ''
    }

    if (!pwd) {
      result.message = __(T.error_messages.password_required)
    } else {
      const compare = await bcrypt.compare(pwd, HARDCODED_PASS)
      if (!compare) {
        result.message = __(T.error_messages.invalid_password)
      } else {
        result.valid = true
      }
    }
    return result
  }

  activateAdmin = async () => {
    const { admin } = this.state
    if (!admin) {
      const adminPwd = prompt('Insert admin password', '')
      const result: PasswordValidate = await this.validateFields(adminPwd)
      if (!result.valid) {
        showToastError(result.message)
        return
      }
    }
    AppStore.setAdminMode(!admin)
    this.setState({ admin: !admin })
  }

  onEmulationClick = async (checked: boolean) => {
    await AppStore.enableEmulation(checked)
    this.forceUpdate()
  }

  onZbpEmulationClick = async (checked: boolean) => {
    await AppStore.enableZbpEmulation(checked)
    this.forceUpdate()
  }

  goUtilities = () => {
    Router.navigate('/utilities', {})
  }

  render() {
    const { onClose, visible } = this.props
    const { admin, endpoint } = this.state
    return (
      <Modal size="lg" onClose={onClose} visible={visible} closeOnOverlayClick>
        <Box vcenter row mb={30}>
          <ModalTitle title={__(T.titles.settings)} />
          <Button
            size="small"
            title={admin ? 'DISABLED ADMIN MODE' : 'ADMIN MODE'}
            onClick={this.activateAdmin}
            variant="secondary"
          />
        </Box>
        {!admin && <KeyValueRow variant="grey" label="Backend" value={endpoint} />}
        {admin && <Input label="Backend" onEnter={AppStore.setEndpoint} defaultValue={endpoint} />}

        <Spacer />

        <KeyValueRow
          variant="grey"
          label={__(T.misc.language)}
          value={this.languages.find((language) => language.value === AppStore.language)?.label}
          optionModal={{
            onSelect: (item) => this.handleChange(item[0]),
            field: 'label',
            options: this.languages ?? [],
            title: __(T.titles.select_language),
            selected: [{ label: this.languages.find((language) => language.value === AppStore.language)?.label }]
          }}
        />

        {admin && enabledUtilities() && (
          <>
            <Spacer />
            <Button size="medium" title="Utilities" onClick={this.goUtilities} variant="secondary" />
          </>
        )}

        {(AppStore.loggedUser?.admin || admin) && (
          <>
            <Spacer />
            <Switch
              onChange={this.onEmulationClick}
              check={AppStore.emulation}
              name="emulation"
              label={__(T.misc.enable_workstation_emulation)}
            />
            <Spacer />
            <Switch
              onChange={this.onZbpEmulationClick}
              check={AppStore.zbpEmulation}
              name="zbpEmulation"
              label={__(T.misc.enable_browser_print_emulation)}
            />
          </>
        )}
      </Modal>
    )
  }
}
